import { motion } from "motion/react";

interface AnalysisProgressOverlayProps {
  progress: number;
  loadingMessage: string;
  estimatedTime: number;
  currentQuote: { text: string; author: string };
}

export function AnalysisProgressOverlay({
  progress,
  loadingMessage,
  estimatedTime,
  currentQuote,
}: AnalysisProgressOverlayProps) {
  return (
    <motion.div
      key="analyzer-loading"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-start w-full pt-16 pb-12"
    >
      <div className="flex flex-col items-center text-center w-full max-w-md">
        <div className="relative w-32 h-32 mb-8">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="60"
              stroke="currentColor"
              strokeWidth="2"
              fill="transparent"
              className="text-ink-200"
            />
            <motion.circle
              cx="64"
              cy="64"
              r="60"
              stroke="currentColor"
              strokeWidth="3"
              fill="transparent"
              strokeDasharray={377}
              initial={{ strokeDashoffset: 377 }}
              animate={{ strokeDashoffset: 377 - (377 * progress) / 100 }}
              transition={{ duration: 0.5, ease: "linear" }}
              className="text-oxblood"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-serif font-bold italic">
              {Math.round(progress)}%
            </span>
            <span className="text-[8px] font-bold uppercase tracking-widest text-ink-500">
              Complete
            </span>
          </div>
        </div>

        <div className="space-y-4 w-full">
          <div className="h-[1px] w-full bg-ink-200 relative overflow-hidden">
            <motion.div
              className="absolute inset-y-0 left-0 bg-oxblood"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "linear" }}
            />
          </div>

          <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-widest text-ink-500 text-left">
            <span>{loadingMessage}</span>
            <span>Est. {estimatedTime}s remaining</span>
          </div>
        </div>

        <motion.blockquote
          key={currentQuote.text}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-10 border-l-2 border-oxblood pl-5 text-left w-full"
        >
          <p className="font-serif text-xl text-ink leading-relaxed">
            {currentQuote.text}
          </p>
          <footer className="mt-3 text-ink-500 text-[10px] font-bold uppercase tracking-widest">
            — {currentQuote.author}
          </footer>
        </motion.blockquote>
      </div>
    </motion.div>
  );
}
